import { useNavigate, useSearchParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { ArrowRightStartOnRectangleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import type { CountingProblem } from '../types/counting';
import type { AnimationState } from '../components/counting/useTrickAnimation';
import { PlayTable } from '../components/counting/PlayTable';
import { howardApi } from '../api/howardApi';
import { isHandsUnavailable } from '../api/handsUnavailable';
import { useSessionGuard } from '../contexts/SessionGuardContext';
import { usePageTitle } from '../hooks/usePageTitle';
// Howard-specific CSS overrides — never touches Counting Practice styles.
// All layout divergences between Howard and Counting Practice (Howard has no
// Question panel, so the PlayTable fills more vertical space) live in this
// sibling .css file so index.css / Counting Practice is unaffected.
import './HowardHandsPage.css';

export function HowardHandsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  usePageTitle('Hands for Howard — Hawkes Bridge');

  const [problems, setProblems] = useState<CountingProblem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Animation state — controls Next button disabling
  const [animationState, setAnimationState] = useState<AnimationState>('INITIAL');

  // Claimed badge — replaces Declarer label in mobile header when claimed hand reaches end
  const [showClaimedBadge, setShowClaimedBadge] = useState(false);

  // Mobile details popup
  const [showDetailsPopup, setShowDetailsPopup] = useState(false);

  // Spec 030: a broken tournament-hand link is a retryable fault — never "No hands available".
  const handsUnavailableMessage = 'Hands for Howard could not be loaded. Please try again.';

  // Load hands on mount — optionally filter to a single hand via ?id= query param
  const filterId = searchParams.get('id');
  const loadHands = useCallback(() => {
    howardApi.startSession()
      .then(response => {
        let items = response.problems;
        if (filterId) {
          items = items.filter(p => String(p.tableResultId) === filterId);
        }
        if (items.length === 0) {
          setError(filterId ? `Hand #${filterId} not found.` : 'No hands available for Howard.');
        } else {
          setProblems(items);
        }
        setLoading(false);
      })
      .catch(err => {
        setError(
          isHandsUnavailable(err)
            ? handsUnavailableMessage
            : err instanceof Error
              ? err.message
              : 'Failed to load Howard hands.'
        );
        setLoading(false);
      });
  }, [filterId]);

  useEffect(() => {
    loadHands();
  }, [loadHands]);

  const handleRetry = useCallback(() => {
    setError(null);
    setLoading(true);
    loadHands();
  }, [loadHands]);

  // Prevent iOS Safari keyboard-triggered zoom from persisting after the
  // keyboard closes. On blur, scroll back to (0,0) so the page isn't left
  // in a scrolled/zoomed state. Safety net for any future inputs or
  // contenteditable elements added to this page.
  useEffect(() => {
    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName !== 'INPUT' && target.tagName !== 'TEXTAREA') return;
      setTimeout(() => {
        if (document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
          window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        }
      }, 100);
    };
    document.addEventListener('focusout', handleFocusOut);
    return () => document.removeEventListener('focusout', handleFocusOut);
  }, []);

  // Scroll to top on problem change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentIndex]);

  // Reset animation state when problem changes
  useEffect(() => {
    setAnimationState('INITIAL');
    setShowClaimedBadge(false);
  }, [currentIndex]);

  const handleAnimationComplete = useCallback(() => {
    // No additional state needed — animationState controls button enabling
  }, []);

  const handleClaimedVisible = useCallback(() => {
    setShowClaimedBadge(true);
  }, []);

  const handleAnimationStateChange = useCallback((state: AnimationState) => {
    setAnimationState(state);
  }, []);

  const handleNext = useCallback(() => {
    if (!problems) return;
    if (currentIndex + 1 >= problems.length) {
      navigate('/data-editing');
      return;
    }
    setCurrentIndex(prev => prev + 1);
  }, [problems, currentIndex, navigate]);

  const handleEndSession = useCallback(() => {
    navigate('/data-editing');
  }, [navigate]);

  // Mobile scoring-type badge in header (mirrors ProblemsPage / CountingPracticePage).
  // setHasActiveSession + setScoringType drive the scoring indicator in Header.tsx.
  const { setHasActiveSession, setScoringType } = useSessionGuard();
  const currentScoring = problems?.[currentIndex]?.scoring ?? null;
  useEffect(() => {
    const sessionActive = !!problems?.length;
    setHasActiveSession(sessionActive);
    setScoringType(sessionActive ? currentScoring : null);
    return () => {
      setHasActiveSession(false);
      setScoringType(null);
    };
  }, [problems, currentScoring, setHasActiveSession, setScoringType]);


  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-ink-soft">Loading Howard's hands...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 h-full">
        <p className="text-red-600">{error}</p>
        {/* Only a broken link is worth retrying — "Hand #N not found" and an empty pool are not. */}
        {error === handsUnavailableMessage && (
          <button
            type="button"
            onClick={handleRetry}
            className="min-h-[24px] min-w-[24px] rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brass"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!problems?.length) {
    return null;
  }

  const problem = problems[currentIndex];
  if (!problem) return null;

  // Next button disabled only while animation is actively playing
  const nextDisabled = animationState === 'PLAYING';
  const isLastHand = currentIndex + 1 >= problems.length;

  return (
    <div className="mx-auto px-2 md:px-4 narrow:px-0.5 py-2 short:py-1 narrow:py-0.5 overflow-hidden flex flex-col counting-page-root" style={{ maxWidth: 'min(100vw - 0.25rem, 70rem)' }}>
      {/* Desktop header */}
      <div className="hidden md:flex narrow:hidden items-center gap-4 short:gap-2 mb-2 short:mb-1 flex-shrink-0">
        <h1 className="text-lg sm:text-xl font-bold text-ink">
          Hands for Howard
        </h1>
        <div className="ml-auto flex items-center gap-4 short:gap-2">
          <div className="flex items-center bg-cream border border-line rounded-lg px-3 py-1.5 gap-3">
            <span className="text-sm text-ink-soft">Hand: {currentIndex + 1}/{problems.length}</span>
          </div>
          <button type="button" onClick={handleEndSession}
            className="text-sm font-medium text-ink bg-cream border border-brass hover:bg-paper rounded-md px-3 py-1.5"
            aria-label="End Session">
            End Session
          </button>
        </div>
      </div>

      {/* Mobile compact controls */}
      <div className="md:hidden narrow:flex flex items-center gap-2 mb-0.5 overflow-hidden flex-shrink-0" data-testid="compact-controls">
        <span className="text-sm">
          <span className="font-bold text-ink">{currentIndex + 1}/{problems.length}</span>
          <span className="text-ink-soft ml-1">#{problem.tableResultId}</span>
        </span>
        <div className="w-px h-4 bg-line-strong" />
        <button type="button" onClick={() => setShowDetailsPopup(prev => !prev)} className="text-ink-soft hover:text-ink-soft p-0.5" aria-label="Problem details">
          <InformationCircleIcon className="h-4 w-4" />
        </button>
        {showClaimedBadge ? (
          <span className="claimed-blink text-xs font-bold flex-shrink-0" style={{ color: '#991b1b' }}>Claimed</span>
        ) : (
          <span className="text-xs font-medium text-ink flex-shrink-0">Declarer: {{ N: 'North', E: 'East', S: 'South', W: 'West' }[problem.declarerDirection]}</span>
        )}
        <button type="button" onClick={handleEndSession} className="ml-auto flex-shrink-0 p-1 text-ink-soft hover:text-ink hover:bg-cream rounded"
          aria-label="End Session" title="End Session">
          <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Mobile Details popup */}
      {showDetailsPopup && (
        <div className="md:hidden narrow:block fixed inset-0 z-40 bg-black/30 flex items-start justify-center pt-16 px-4" onClick={() => setShowDetailsPopup(false)}>
          <div className="bg-card rounded-lg shadow-lg p-4 w-full max-w-sm relative" onClick={e => e.stopPropagation()}>
            <button type="button" onClick={() => setShowDetailsPopup(false)} className="absolute top-1 right-1 text-ink-soft hover:text-ink-soft text-sm font-bold px-1" aria-label="Close">&times;</button>
            <h3 className="text-sm font-semibold text-ink mb-2">Hand Details</h3>
            <div className="space-y-1 text-sm text-ink">
              <div><span className="text-ink-soft">Number:</span> <span className="font-medium">#{problem.tableResultId}</span></div>
              <div><span className="text-ink-soft">Scoring:</span> <span className="font-medium">{problem.scoring || 'N/A'}</span></div>
              <div><span className="text-ink-soft">From:</span> <span className="font-medium">{problem.tournamentName}</span></div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop: Metadata row */}
      <div className="hidden md:block narrow:hidden mt-1 short:mt-0.5 mb-1 short:mb-0.5 flex-shrink-0">
        <div className="bg-card border border-line rounded-lg p-2 short:p-1 flex flex-wrap gap-4 short:gap-3 text-sm short:text-xs">
          <span>
            <span className="text-ink-soft">Number:</span>{' '}
            <span className="font-semibold text-ink">{problem.tableResultId}</span>
          </span>
          <span className="text-red-500 font-bold">
            <span>Scoring:</span>{' '}
            <span>{problem.scoring || 'N/A'}</span>
          </span>
          <span className="hidden md:inline narrow:hidden">
            <span className="text-ink-soft">From:</span>{' '}
            <span className="font-medium text-ink">{problem.tournamentName}</span>
          </span>
        </div>
      </div>

      {/* Main content — PlayTable only, no question panel */}
      <div className="bg-cream rounded-lg narrow:rounded p-2 short:p-1.5 narrow:p-0.5 mb-1 short:mb-0.5 flex-1 min-h-0 flex flex-col overflow-hidden counting-main-content">
        <div className="flex gap-2 short:gap-1.5 narrow:gap-1 narrow:flex-col flex-1 min-h-0 overflow-hidden counting-content-row">
          <div className="flex-1 min-h-0 overflow-hidden counting-play-col howard-play-col">
            <PlayTable
              problem={problem}
              onAnimationComplete={handleAnimationComplete}
              onAnimationStateChange={handleAnimationStateChange}
              onClaimedVisible={handleClaimedVisible}
              isHowardPage
              // E2E-only escape hatch: when the URL has `?autoplay=true`, skip
              // the INITIAL-state Play overlay and start the animation
              // immediately. This matches the Counting Practice page's
              // behavior and lets the Howard responsive-layout test
              // deterministically reach STOPPED-at-last-trick state on mobile
              // emulation projects (mobile-chrome / mobile-safari), where
              // synthetic clicks on the overlay Play button are unreliable.
              autoplay={searchParams.get('autoplay') === 'true'}
            />
          </div>
        </div>
      </div>

      {/* Next Hand button — enabled only after all tricks played.
          howard-next-btn-wrapper: on portrait mobile/tablet, CSS gives this
          wrapper `margin-top: auto` so it sticks to the bottom of
          counting-page-root — mirrors the Skip/Next placement on Counting
          Practice, which is pushed to the bottom by the flex-1 main-content
          above it. Howard's main-content is sized to content on portrait
          (see HowardHandsPage.css gap-collapse fix), so we pin the button
          explicitly instead. */}
      <div className="flex-shrink-0 pt-0.5 howard-next-btn-wrapper">
        <button
          type="button"
          onClick={handleNext}
          disabled={nextDisabled}
          className={`px-4 py-2 short:px-3 short:py-1 short:text-sm rounded-md font-medium ${
            nextDisabled
              ? 'bg-paper text-ink-soft cursor-not-allowed'
              : 'bg-primary-600 text-white hover:bg-primary-700'
          }`}
        >
          {isLastHand ? 'Finish' : 'Next Hand'}
        </button>
      </div>

    </div>
  );
}
